import { DeleteOutline } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Chip,
  alpha,
} from '@mui/material';
import type {
  IBuilderTransactionOption,
  ILegalDependencyConditionRowProps,
  PhaseStatus,
} from '../../../Interfaces/ILegalDependency';
import { HierarchicalTargetAutocomplete } from './HierarchicalTargetAutocomplete';
import { PHASE_STATUS_OPTIONS, getStatusFieldSx } from './utils';

export const LegalDependencyConditionRow = ({
  condition,
  conditionIndex,
  getRequirementOption,
  getStatusLabel,
  getTransactionOption,
  isLoadingLegalTransactions,
  legalRequirementStates,
  onChange,
  onRemove,
  removeDisabled,
  requirementOptions,
  transactionLoading,
  transactionOptions,
}: ILegalDependencyConditionRowProps) => {
  const selectedRequirementHasError =
    condition.targetType === 'requirement' &&
    condition.idLegalRequirement !== null
      ? (requirementOptions.find(
          o => o.idLegalRequirement === condition.idLegalRequirement
        )?.hasError ?? true)
      : true;
  const filteredStates = selectedRequirementHasError
    ? legalRequirementStates
    : legalRequirementStates.filter(s => s.idLegalRequirementState !== 30);

  return (
    <Box
      sx={{
        border: theme => `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        borderRadius: 2,
        p: 1.5,
        bgcolor: theme => alpha(theme.palette.background.paper, 0.85),
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'
        >
          <Chip
            label={conditionIndex === 0 ? 'Bedingung' : 'UND'}
            size='small'
            variant='outlined'
          />
          <Stack alignItems='center' direction='row' spacing={0.5}>
            {transactionLoading ? <CircularProgress size={18} /> : null}
            <IconButton disabled={removeDisabled} onClick={onRemove}>
              <DeleteOutline />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ px: 1.5 }}>
          <Grid alignItems='center' container spacing={1.5}>
            <Grid item xs={12} lg={4}>
              <Autocomplete<IBuilderTransactionOption, false, false, false>
                disabled={isLoadingLegalTransactions}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_, value) =>
                  onChange({
                    transactionId: value?.value ?? null,
                    idLegalRequirement: null,
                    idLegalPhase: null,
                    idLegalRequirementState: null,
                    PhaseIsCompleted: null,
                  })
                }
                options={transactionOptions}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Vorgang'
                    placeholder='Vorgang suchen'
                    size='small'
                  />
                )}
                size='small'
                value={getTransactionOption(condition.transactionId)}
              />
            </Grid>
            <Grid item xs={12} lg={5}>
              <HierarchicalTargetAutocomplete
                disabled={
                  condition.transactionId === null || transactionLoading
                }
                label='Bezug'
                noOptionsText={
                  condition.transactionId === null
                    ? 'Bitte zuerst einen Vorgang wählen'
                    : 'Keine passenden Phasen oder Maßnahmen gefunden'
                }
                onChange={value =>
                  onChange({
                    targetType: value?.targetType ?? condition.targetType,
                    idLegalRequirement: value?.idLegalRequirement ?? null,
                    idLegalPhase: value?.idLegalPhase ?? null,
                    idLegalRequirementState: null,
                    PhaseIsCompleted: null,
                  })
                }
                options={requirementOptions}
                placeholder='Phase oder Maßnahme suchen'
                value={getRequirementOption(
                  condition.transactionId,
                  condition.targetType,
                  condition.idLegalRequirement,
                  condition.idLegalPhase
                )}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <TextField
                disabled={
                  condition.transactionId === null ||
                  (condition.targetType === 'requirement'
                    ? condition.idLegalRequirement === null
                    : condition.idLegalPhase === null)
                }
                fullWidth
                label='Status'
                onChange={event =>
                  onChange(
                    condition.targetType === 'requirement'
                      ? {
                          idLegalRequirementState:
                            event.target.value === ''
                              ? null
                              : Number(event.target.value),
                          PhaseIsCompleted: null,
                        }
                      : {
                          idLegalRequirementState: null,
                          PhaseIsCompleted:
                            event.target.value === ''
                              ? null
                              : (Number(event.target.value) as PhaseStatus),
                        }
                  )
                }
                select
                size='small'
                sx={getStatusFieldSx(
                  getStatusLabel(
                    condition.targetType,
                    condition.idLegalRequirementState,
                    condition.PhaseIsCompleted
                  )
                )}
                value={
                  condition.targetType === 'requirement'
                    ? (condition.idLegalRequirementState ?? '')
                    : (condition.PhaseIsCompleted ?? '')
                }
              >
                <MenuItem value=''>Status wählen</MenuItem>
                {condition.targetType === 'requirement'
                  ? filteredStates.map(state => (
                      <MenuItem
                        key={state.idLegalRequirementState}
                        value={state.idLegalRequirementState}
                      >
                        {state.LegalRequirementState}
                      </MenuItem>
                    ))
                  : PHASE_STATUS_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};
